import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import {
  AgentTurnStatusSchema,
  type AgentTurnStatus,
  wireTurnRecord,
} from "./turn-status-schema.js";
import { conversationSync } from "./sync-session.js";
import { reportMalformedFrame } from "./malformed-frame.js";

// knowledge/126 step 2b: per agent, the turn status with the highest statusRev wins.

// How many dropped agents keep their last revision. One number each, oldest out first.
const MAX_REVISION_FLOORS = 2000;

export class TurnStatusBook {
  #held = new SvelteMap<string, AgentTurnStatus>();
  // The last revision of an agent the roster dropped: a delayed older frame must still lose.
  #floors = new Map<string, number>();

  apply(agentId: string, status: unknown): boolean {
    const parsed = AgentTurnStatusSchema.safeParse(status);
    if (!parsed.success) {
      // Only undefined is absent (an older daemon): the field is optional, not nullable.
      if (status !== undefined) reportMalformedFrame("turnStatus", agentId);
      return false;
    }
    const floor = this.#held.get(agentId)?.statusRev ?? this.#floors.get(agentId) ?? 0;
    if (parsed.data.statusRev <= floor) return false;
    this.#floors.delete(agentId);
    this.#held.set(agentId, parsed.data);
    return true;
  }

  current(agentId: string): AgentTurnStatus | undefined {
    return this.#held.get(agentId);
  }

  // Undefined while no daemon published a status (an older one): the caller keeps its own signal.
  liveFor(agentId: string, sessionId: string): boolean | undefined {
    const status = this.#held.get(agentId);
    if (!status) return undefined;
    return status.lifecycle === "running" && status.turnSession === sessionId;
  }

  // Drops every agent a roster no longer lists, keeping only its revision as a floor.
  retain(agentIds: ReadonlySet<string>): void {
    for (const [agentId, status] of this.#held) {
      if (agentIds.has(agentId)) continue;
      this.#held.delete(agentId);
      this.#floors.set(agentId, status.statusRev);
    }
    for (const oldest of this.#floors.keys()) {
      if (this.#floors.size <= MAX_REVISION_FLOORS) break;
      this.#floors.delete(oldest);
    }
  }

  // A reset forgets the floors too: they are revisions of the account being left.
  reset(): void {
    this.#held.clear();
    this.#floors.clear();
  }

  size(): number {
    return this.#held.size;
  }
}

export const turnStatusBook = conversationSync.own("turnStatus", new TurnStatusBook());

// LIVE of a session body (#3618). A turn cannot be running on a machine that is gone, and a
// machine that is gone cannot send the frame that says so: an offline daemon outranks the
// held status. `daemonOnline` undefined = the agent's machine is not known here.
export function sessionBodyIsLive(input: {
  book: TurnStatusBook;
  agentId: string;
  sessionId: string;
  daemonOnline: boolean | undefined;
  fallback: () => boolean;
}): boolean {
  if (input.daemonOnline === false) return false;
  return input.book.liveFor(input.agentId, input.sessionId) ?? input.fallback();
}

interface RosterRow {
  agentId: string;
  turnRecord?: unknown;
  turnStatus?: unknown;
}

// Every roster snapshot applies under the same rule and is the set of agents the book keeps.
export function followRosterTurnStatus(
  book: TurnStatusBook,
  rows: () => readonly RosterRow[],
): () => void {
  return $effect.root(() => {
    $effect(() => {
      const listed = rows();
      untrack(() => adoptRoster(book, listed));
    });
  });
}

function adoptRoster(book: TurnStatusBook, listed: readonly RosterRow[]): void {
  book.retain(new Set(listed.map((row) => row.agentId)));
  for (const row of listed) book.apply(row.agentId, wireTurnRecord(row));
}
